(ns app.front.view.group.admin.common
  "Reusable admin UI elements"
  (:require [app.front.ui :as ui]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.ui.util :as uiu]
            [app.front.util :as futil]
            [app.front.view.group.admin.subs :as gas]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [reagent.core :as r]
            [schema.core :as s]))

(defn form-group [& args]
  (let [[{:keys [legend-text]} children] (uiu/xtract-args args)]
    (cc/form-group {:legend-text (or legend-text "")}
                   (->> children
                        (into [(if (-> children count (> 7))
                                 :div.admin-form-cols
                                 ;; for seven or less items, give al children a separate row
                                 :div)])))))


(defn form
  "An administration page form

  Give opts `:title` and `:legend-text`."
  [& args]
  (let [[{:keys [title] :as opts} children] (uiu/xtract-args args)]
    [:<>
     (when title
       [:h3.mb-2.pl-0 title])
     [:form
      [:div.mb-4
       [form-group opts children]]]]))


(defn admin-config-form
  [& children]
  (into [:form.admin-config-form
         {:on-key-press #(when (and (futil/enter-press? %)
                                    (not (futil/target-element-of-type? % "textarea")))
                           (.preventDefault %))
          :on-submit    #(.preventDefault %)}]
        children))


(defn- section-common
  [section-element title-element {:keys [title class hide-title?]} & children]
  [eb/error-boundary
   (into [section-element
          {:class (str class)}
          (when (and title
                     (not hide-title?)) [title-element title])]
         children)])


(def ^{:arglists '([{:keys [title class hide-title?]} & children])} section
  "An administration page section"
  (partial section-common :section :h2))

(def ^{:arglists '([{:keys [title class hide-title?]} & children])} section-title-wrapper
  (partial section-common :div.mt-6.mb-4 :h2))

(defn header-with-language-selector [title]
  [:div.flex.flex-row.justify-between.items-center.-mt-4
   [:h2.m-0 title]
   [ui-i18n/edit-language-selector {:allowed-locales-sub ::gas/allowed-locales
                                    :dropdown-class      "bg-white"}]])




(def ^{:arglists '([{:keys [title class]} & children])} section-title-wrapper-with-language-selector
  (partial section-common :div.mt-6.mb-4 header-with-language-selector))




(defn admin-page
  "The frame and header of an administrator page. Consider using admin-page-v2 for new pages."
  [{:keys [match title wait-for-group? class]} & children]
  (let [group-name (<sub [::gas/group-name])
        tr         (<sub [::gas/group-tr])
        tr-quiet   (<sub [::gas/group-tr-quiet])
        heading    [:h1.px-8 (str title (when group-name ": ") group-name)]]
    (into [:div.cloudpermit-page.admin-page
           {:class class}
           [ui/page-header (util/mmap match tr tr-quiet) heading]]
          (if (and wait-for-group?
                   (not group-name))
            [[:div.flex.w-full.justify-center.mt-8
              (cc/loading-spinner {:with-overlay false})]]
            children))))


(defn admin-page-v2
  "Layout with horizontally divided bg color for municipality admin pages.

  IMPORTANT: The route using this component must set `:own-layout? true` in its route data.
  Without it the `main-wrapper` adds an extra `main.bx--grid` that clips the branded
  background from extending to the page edges.

  `children` are only rendered if `tabs` are not provided.

  Preferred pattern for pages with tabs: manage tab state with your own re-frame event/sub pair,
  pass a `[ui-tabs/tabs ...]` as `:header-content`, and render the active tab content using
  `section-title-wrapper` as a child. See `app.front.view.group.admin.branding.core` for a
  reference implementation. When the tabs switch between routes, give each tab an `:href`
  instead of using `:on-change`, see `app.front.view.group.admin.templates`.

  Required keys
  :match                route match from reitit router

  Optional keys
  :title                string or a keyword for tr. If missing, route name kw from match will be used
  :heading              hiccup replacing the default `<h1>`, for pages that need a richer heading
                        (status tags, a subtitle, ...). `:title` is then only used as a fallback,
                        e.g. while the page's data is still loading.
  :wait-for-group?      if true, show spinner until `:group/name` has been loaded
  :header-content       hiccup content rendered above the divider line (not shown when `:tabs` is provided)
  :allowed-group-types  set of group types, e.g. `#{:group.type/domain}` to limit view visibility to domain groups
  :tabs                 A sequence of tuples `[[tab-tr-key view-component-ref on-select-fn] ...]`
                        Tab views are rendered instead of any other `children`.
                        First tab will be the default view, and if multiple tabs are present, a tab bar
                        is rendered above the divider line."
  [{:keys [match title wait-for-group? header-content allowed-group-types tabs hide-title? heading]} & children]
  (r/with-let [*current-tab-id (r/atom (ffirst tabs))]
    (let [group-name  (<sub [::gas/group-name])
          group-type  (<sub [::gas/group-type])
          tr          (<sub [::gas/group-tr])
          tr-quiet    (<sub [::gas/group-tr-quiet])
          title-str   (cond
                        (keyword? title) (tr [title])
                        (string? title) title
                        :else (some-> match :data :name vector tr))
          heading     (or heading
                          [:h1.my-10.font-semibold
                           (str title-str (when group-name ": ") group-name)])
          current-tab (when (seq tabs)
                        (or (p/find-first tabs (comp (partial = @*current-tab-id) first))
                            (first tabs)))]
      [:div.branded-admin-background-color.cloudpermit-page.h-full
       [:header.branded-background-color.border-b.border-solid.border-ui-violet
        {:style {:padding-bottom "0.05px"}} ;; Prevent collapsing heading margin without having to use overflow: hidden
        (into [:div.bx--grid.max
               [ui/page-header (util/mmap match tr tr-quiet) heading]]
              (if (next tabs)
                [[ui-tabs/tabs
                  {:selected-id (first current-tab)
                   :aria-label  title-str
                   :on-change   (fn [id]
                                  (reset! *current-tab-id id)
                                  (when-let [on-select-fn (some-> (p/find-first tabs (comp (partial = id) first))
                                                                  (nth 2 nil))]
                                    (on-select-fn)))
                   :tabs        (for [[id _ _] tabs]
                                  {:id    id
                                   :label (tr [id])})}]]
                [header-content]))]
       (into [:main.admin-page-content.bx--grid.max.relative.pt-0]
             (cond
               (and (or wait-for-group?
                        (seq allowed-group-types))
                    (not group-name))
               [[:div.flex.w-full.justify-center.mt-8
                 (cc/loading-spinner {:with-overlay false})]]
               (and (seq allowed-group-types)
                    (not (contains? allowed-group-types group-type)))
               [[:div
                 [ui/warning (tr [:view/not-available-for-type]
                                 [(tr [group-type])])]]]

               current-tab
               (let [[tr-k content] current-tab]
                 [[section-title-wrapper
                   {:title       (tr [tr-k])
                    :hide-title? hide-title?}
                   [(or content :div)]]])

               :else
               children))])))


(defn- config-section-options? [v]
  (and (map? v)
       (or (contains? v :collapsible?)
           (contains? v :default-collapsed?)
           (contains? v :section-id))))


(defn config-section [title description & args]
  (let [[{:keys [collapsible? default-collapsed? section-id]} children]
        (if (or (nil? (first args))
                (config-section-options? (first args)))
          [(first args) (rest args)]
          [{} args])]
    (r/with-let [*collapsed? (r/atom (boolean (and collapsible? default-collapsed?)))]
      (let [tr              (<sub [::gas/group-tr])
            collapsed?      (and collapsible? @*collapsed?)
            section-id      (or section-id (futil/sanitize-id title))
            section-data-cy (str "config-section-" section-id)]
        [eb/error-boundary
         [:section {:data-cy section-data-cy}
          (if collapsible?
            [:div.flex.items-center.justify-between.gap-4
             [:h2.m-0 title]
             [ui/expand-button
              {:button-props    {:data-cy    (str section-data-cy "-toggle")
                                 :type       :button
                                 :class-name "normal-case font-normal"}
               :expand-handler  #(swap! *collapsed? not)
               :title-expanded  (tr [:close])
               :title-collapsed (tr [:open])
               :expanded?       (not collapsed?)
               :tr              tr}]]
            [:div
             [:h2 title]])
          (when-not collapsed?
            [:div {:data-cy (str section-data-cy "-content")}
             [:div.bx--row
              (if (string? description)
                [:p.admin-page__instruction.bx--col-bx-12
                 description]
                description)]
             [:div.bx--row.mt-8
              (into [:div.bx--col-xs-12]
                    children)]])]]))))


(defn group-has-role? [role group]
  (-> group :group/roles set role boolean))


(def write-auth-group? (partial group-has-role? :group.role/write-auth))
(def viewer-group? (partial group-has-role? :group.role/viewer))
(def reporting-group? (partial group-has-role? :group.role/reporting))
(def org-admin-group? (partial group-has-role? :group.role/domain-admin))
(def escrow-admin-group? (partial group-has-role? :group.role/escrow-admin))


(defn read-only-group? [group]
  (reporting-group? group))


(defn write-group? [group]
  (and (-> group read-only-group? not)
       (-> group viewer-group? not)))


(defn user-selectable-group? [group]
  (and (-> group write-auth-group? not)
       (-> group viewer-group? not)))


(defn group-sort [g1 g2]
  (cond (write-auth-group? g1) 1
        (write-auth-group? g2) -1
        :else (compare (:group/name g1) (:group/name g2))))


(defn form-item [tr k v]
  [:div.mt-3.pr-6
   [:div.bx--label
    (tr [k])]
   [:div (or v (tr [:gis-editor/no-value]))]])

(defn checkbox
  "A checkbox that directly controls a boolean group-config attribute in the context of the config editor.
   Optional :on-change in editor-ctx is called after the swap with the new value."
  [{:keys [tr *group-config parent-config target on-change]} attr]
  (cc/checkbox {:id         (str (name attr) target)
                :label-text (tr [attr])
                :checked    (or (get parent-config attr)
                                (get @*group-config attr)
                                false)
                :disabled   (boolean (get parent-config attr))
                :on-change  (fn [checked?]
                              (swap! *group-config assoc attr checked?)
                              (when on-change
                                (on-change checked?)))}))

(defn standard-route-definition [{:keys [name view controllers]}]
  (util/mmap {:parameters  {:path {:group-id s/Int}}
              :own-layout? true}
             name view controllers))
